/**
 * Business Capability Validation Schemas
 */

import { z } from 'zod';
import { emptyToUndefined, nameField, nanToUndefined, optionalBoundedText } from './shared';

export const capabilityValidationRules = {
  name: nameField(
    200,
    'Capability name is required',
    'Capability name must be less than 200 characters'
  ),

  description: optionalBoundedText(1000, 'Description must be less than 1000 characters'),

  parentCapabilityId: z
    .union([
      z.string().regex(/^cap-/, 'Parent capability ID must start with "cap-"'),
      z.literal(''),
    ])
    .optional(),

  businessOwnerId: z.string().min(1, 'Business owner is required'),

  owningBusinessUnitId: z.string().min(1, 'Owning business unit is required'),

  notes: optionalBoundedText(2000, 'Notes must be less than 2000 characters'),

  assumptions: optionalBoundedText(2000, 'Assumptions must be less than 2000 characters'),
};

export const createCapabilitySchema = z.object({
  name: capabilityValidationRules.name,
  description: capabilityValidationRules.description,
  level: z.number({ error: 'Level is required' }).int().min(1, 'Level must be at least 1'),
  parentCapabilityId: capabilityValidationRules.parentCapabilityId,
  criticality: z.enum(['critical', 'important', 'supporting', 'nice_to_have'], {
    message: 'Criticality is required',
  }),
  strategicImportance: z.enum(['strategic', 'core', 'supporting'], {
    message: 'Strategic importance is required',
  }),
  lifecycleStatus: z.enum(['planned', 'developing', 'active', 'mature', 'deprecated', 'retired'], {
    message: 'Lifecycle status is required',
  }),
  investmentPriority: emptyToUndefined(z.enum(['high', 'medium', 'low', 'hold']).optional()),
  businessOwnerId: capabilityValidationRules.businessOwnerId,
  owningBusinessUnitId: capabilityValidationRules.owningBusinessUnitId,
  stakeholderIds: z.array(z.string().min(1)).optional().default([]),
  maturityLevel: nanToUndefined(z.number().int().min(1).max(5).optional()),
  targetMaturityLevel: nanToUndefined(z.number().int().min(1).max(5).optional()),
  currentInvestmentLevel: emptyToUndefined(
    z.enum(['none', 'maintain', 'enhance', 'transform']).optional()
  ),
  targetInvestmentLevel: emptyToUndefined(
    z.enum(['none', 'maintain', 'enhance', 'transform']).optional()
  ),
  planningHorizon: z.string().optional(),
  notes: capabilityValidationRules.notes,
  assumptions: capabilityValidationRules.assumptions,
  tags: z.array(z.string().min(1)).optional().default([]),
});

export const updateCapabilitySchema = z
  .object({
    name: capabilityValidationRules.name.optional(),
    description: capabilityValidationRules.description,
    level: z.number().int().min(1).optional(),
    parentCapabilityId: capabilityValidationRules.parentCapabilityId,
    criticality: z.enum(['critical', 'important', 'supporting', 'nice_to_have']).optional(),
    strategicImportance: z.enum(['strategic', 'core', 'supporting']).optional(),
    lifecycleStatus: z
      .enum(['planned', 'developing', 'active', 'mature', 'deprecated', 'retired'])
      .optional(),
    investmentPriority: emptyToUndefined(z.enum(['high', 'medium', 'low', 'hold']).optional()),
    businessOwnerId: capabilityValidationRules.businessOwnerId.optional(),
    owningBusinessUnitId: capabilityValidationRules.owningBusinessUnitId.optional(),
    stakeholderIds: z.array(z.string().min(1)).optional(),
    maturityLevel: nanToUndefined(z.number().int().min(1).max(5).optional()),
    targetMaturityLevel: nanToUndefined(z.number().int().min(1).max(5).optional()),
    currentInvestmentLevel: emptyToUndefined(
      z.enum(['none', 'maintain', 'enhance', 'transform']).optional()
    ),
    targetInvestmentLevel: emptyToUndefined(
      z.enum(['none', 'maintain', 'enhance', 'transform']).optional()
    ),
    planningHorizon: z.string().optional(),
    notes: capabilityValidationRules.notes,
    assumptions: capabilityValidationRules.assumptions,
    tags: z.array(z.string().min(1)).optional(),
  })
  .refine((data) => Object.values(data).some((value) => value !== undefined), {
    message: 'At least one field must be updated',
  });

export type CreateCapabilityFormData = z.infer<typeof createCapabilitySchema>;

export type UpdateCapabilityFormData = z.infer<typeof updateCapabilitySchema>;
